/* ════ CATEGORY UI + MANAGER ═══════════════════════════════ */
import { state } from './state.js';
import { $ } from './dom.js';
import { DEFAULT_CATS } from './config.js';
import { allCats, customCats, isBuiltin, catVars } from './categories.js';
import { sb } from './supabase/client.js';
import { setSyncDot } from './supabase/data.js';
import { showToast } from './utils/toast.js';
import { buildGantt } from './gantt/index.js';
import { renderList } from './listview.js';
import { renderIdeas } from './ideas.js';

const esc = s => String(s || '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

/* ── Chips (task + idea modals) ──────────────────────────── */
function chip(c, handler) {
  const cls = isBuiltin(c.id) ? c.id : 'cat-custom';
  const style = isBuiltin(c.id) ? '' : catVars(c.id);
  return `<div class="cat-chip ${cls}" data-c="${c.id}" style="${style}" onclick="${handler}(this)">${c.icon ? c.icon + ' ' : ''}${esc(c.name)}</div>`;
}
export function renderCatChips() {
  [['task-cat-row', 'pickCat'], ['idea-cat-row', 'pickIdeaCat']].forEach(([id, handler]) => {
    const el = $(id); if (!el) return;
    el.innerHTML = allCats().map(c => chip(c, handler)).join('')
      + `<div class="cat-chip cat-add" onclick="openCatManager()" title="Add / manage categories">＋</div>`;
  });
}

export function renderLegend() {
  const el = $('leg-items'); if (!el) return;
  el.innerHTML = allCats().map(c => `<div class="leg-i"><div class="leg-d" style="background:${c.color}"></div>${esc(c.name)}</div>`).join('');
}

export function populateFilterCats() {
  const sel = $('flt-cat'); if (!sel) return;
  const cur = state.filters.cat;
  sel.innerHTML = '<option value="all">All categories</option>'
    + allCats().map(c => `<option value="${c.id}">${c.icon || ''} ${esc(c.name)}</option>`).join('');
  sel.value = (cur === 'all' || allCats().some(c => c.id === cur)) ? cur : 'all';
}

// Rebuild everything category-dependent (called on load + when categories change).
export function refreshCategories() {
  renderCatChips(); renderLegend(); populateFilterCats();
  if (state.currentView === 'gantt') buildGantt();
  else if (state.currentView === 'list') renderList();
  else if (state.currentView === 'ideas') renderIdeas();
}

/* ── Manager modal ───────────────────────────────────────── */
function renderCatList() {
  const wrap = $('cat-list'); if (!wrap) return;
  wrap.innerHTML =
    DEFAULT_CATS.map(c => `<div class="cat-item builtin">
        <span class="cat-sw" style="background:${c.color}"></span>
        <span class="cat-nm">${c.icon || ''} ${esc(c.name)}</span>
        <span class="cat-builtin-tag">built-in</span>
      </div>`).join('')
    + customCats().map(c => `<div class="cat-item" data-id="${c.id}">
        <input type="color" class="cat-color" value="${c.color}" title="Colour">
        <input type="text" class="cat-icon" value="${esc(c.icon || '')}" maxlength="2" placeholder="◦" title="Emoji (optional)">
        <input type="text" class="cat-name" value="${esc(c.name)}">
        <button class="cat-del" title="Delete category">✕</button>
      </div>`).join('');
}

export function openCatManager() { renderCatList(); $('cat-modal').classList.add('open'); }
function closeCatManager() { $('cat-modal').classList.remove('open'); }

async function addCategory() {
  const name = $('cat-new-name').value.trim(); if (!name) { showToast('Enter a category name'); return; }
  const color = $('cat-new-color').value || '#6B7280';
  const icon = $('cat-new-icon').value.trim();
  const id = 'c-' + (name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '').slice(0, 24) || 'cat') + '-' + Date.now().toString(36);
  setSyncDot('busy');
  const { data, error } = await sb.from('categories').insert({ id, name, color, icon, sort: 100 + customCats().length }).select();
  if (error) { setSyncDot('err'); showToast('Add failed: ' + error.message); return; }
  if (data?.[0] && !state.categories.find(c => c.id === id)) state.categories.push(data[0]);
  setSyncDot('ok'); showToast('Category added ✓');
  $('cat-new-name').value = ''; $('cat-new-icon').value = '';
  renderCatList(); refreshCategories();
}
async function updateCategory(id, patch) {
  const c = state.categories.find(x => x.id === id); if (c) Object.assign(c, patch);
  setSyncDot('busy');
  const { error } = await sb.from('categories').update(patch).eq('id', id);
  if (error) { setSyncDot('err'); showToast('Save failed: ' + error.message); return; }
  setSyncDot('ok'); refreshCategories();
}
async function deleteCategory(id) {
  const usedT = state.tasks.filter(t => t.cat === id).length;
  const usedI = state.ideas.filter(i => i.cat === id).length;
  const used = usedT + usedI;
  const msg = used
    ? `Delete this category? ${used} card${used === 1 ? '' : 's'} using it will move to “None”.`
    : 'Delete this category?';
  if (!confirm(msg)) return;
  setSyncDot('busy');
  // Reassign anything using it to None so nothing is orphaned.
  if (usedT) { await sb.from('tasks').update({ cat: 'uncat' }).eq('cat', id); state.tasks.forEach(t => { if (t.cat === id) t.cat = 'uncat'; }); }
  if (usedI) { await sb.from('ideas').update({ cat: 'uncat' }).eq('cat', id); state.ideas.forEach(i => { if (i.cat === id) i.cat = 'uncat'; }); }
  const { error } = await sb.from('categories').delete().eq('id', id);
  if (error) { setSyncDot('err'); showToast('Delete failed: ' + error.message); return; }
  state.categories = state.categories.filter(c => c.id !== id);
  setSyncDot('ok'); showToast(used ? `Category deleted · ${used} moved to None` : 'Category deleted');
  renderCatList(); refreshCategories();
}

export function initCatManager() {
  renderCatChips(); renderLegend(); populateFilterCats();
  const modal = $('cat-modal'); if (!modal) return;
  $('cat-add-btn').addEventListener('click', addCategory);
  $('cat-new-name').addEventListener('keydown', e => { if (e.key === 'Enter') addCategory(); });
  $('cat-x').addEventListener('click', closeCatManager);
  $('cat-done').addEventListener('click', closeCatManager);
  modal.addEventListener('click', e => { if (e.target === modal) closeCatManager(); });
  // Delegated edits on the custom list.
  $('cat-list').addEventListener('change', e => {
    const row = e.target.closest('.cat-item[data-id]'); if (!row) return;
    const id = row.dataset.id;
    if (e.target.classList.contains('cat-color')) updateCategory(id, { color: e.target.value });
    else if (e.target.classList.contains('cat-icon')) updateCategory(id, { icon: e.target.value.trim() });
    else if (e.target.classList.contains('cat-name')) { const v = e.target.value.trim(); if (v) updateCategory(id, { name: v }); }
  });
  $('cat-list').addEventListener('click', e => {
    const btn = e.target.closest('.cat-del'); if (!btn) return;
    deleteCategory(btn.closest('.cat-item').dataset.id);
  });
}
